import { MobileSheet } from "@ui/components/MobileSheet";
import { popoverSurface } from "@ui/components/PopoverSurface";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import { Popover } from "@ui/primitives/Popover";
import type { RefObject } from "react";

/** The six that cover almost every reaction anyone actually sends. A longer list is a search box. */
export const REACTION_EMOJI = ["😊", "❤️", "👍", "👏", "🤣", "😮"] as const;
export type ReactionEmoji = (typeof REACTION_EMOJI)[number];

const reactionPicker = tv({
  slots: {
    row: "flex items-center gap-1",
    option: [
      "flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full text-2xl leading-none outline-none",
      "transition-transform hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-red-300",
      "motion-safe:pressed:scale-95 motion-safe:hover:scale-115",
    ],
    popover: "px-2 py-1.5",
    // The floating-sheet card, matching the iOS action sheet's group: a
    // deliberately lighter elevation than --shadow-sheet, because it floats in
    // the sheet's gutters rather than meeting the screen edge.
    sheetPanel: "flex justify-center gap-1 rounded-3xl bg-white p-2 shadow-[0px_3px_20px_0px_rgba(0,0,0,0.08)]",
  },
});

export interface ReactionPickerProps {
  /** Accessible name for the picker surface. */
  label: string;
  onSelect: (emoji: ReactionEmoji) => void;
  onOpenChange: (isOpen: boolean) => void;
  isOpen: boolean;
  /** Anchor for the desktop popover. Ignored on mobile, which presents a sheet instead. */
  triggerRef: RefObject<HTMLElement | null>;
}

/**
 * The six-emoji reaction row, isOpen where each platform expects it: anchored to the message on
 * desktop, and as a bottom sheet on mobile, where a popover would fight the keyboard and the
 * viewport edges and a thumb cannot reach the top of the thread anyway.
 *
 * Platform split mirrors `ActionMenu`. The options themselves are one shared row.
 */
export function ReactionPicker({ label, onSelect, onOpenChange, isOpen, triggerRef }: ReactionPickerProps) {
  const styles = reactionPicker();
  const isDesktop = useBreakpoint("md");

  // The surface presenting this row carries the accessible name; the row itself is layout.
  const options = (
    <div className={styles.row()}>
      {REACTION_EMOJI.map((emoji) => (
        <Button
          key={emoji}
          aria-label={emoji}
          className={styles.option()}
          onPress={() => {
            onSelect(emoji);
            onOpenChange(false);
          }}
        >
          <span aria-hidden="true">{emoji}</span>
        </Button>
      ))}
    </div>
  );

  if (isDesktop) {
    return (
      <Popover
        aria-label={label}
        className={popoverSurface({ className: styles.popover() })}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        placement="top"
        triggerRef={triggerRef}
      >
        {options}
      </Popover>
    );
  }

  return (
    <MobileSheet label={label} onOpenChange={onOpenChange} presentation="floating" isOpen={isOpen}>
      <div className={styles.sheetPanel()}>{options}</div>
    </MobileSheet>
  );
}
